/* istanbul ignore file */
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { Observable, concatMap, forkJoin, map, of } from 'rxjs';
import { ICategory } from '../../model/device-inventory/ICategory';
import { GoogleAnalyticsService } from './google-tag-manager.service';
import { PGHOktaService } from './pghokta.service';
import { PreInitSettingsService } from './settings/pre-init-setting.service';
import { Deductible, DeviceTypeDto } from './device-inventory/device-type.dto';
import { CreateDeviceBodyDto } from './device-inventory/create-device-body.dto';
import { DeviceDto } from './device-inventory/device.dto';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { InventoryInfoDto } from './device-inventory/inventory-info.dto';
import { UpdateDeviceBodyDto } from './device-inventory/update-device-body.dto';
import { buildDeviceDisplayName } from '../../shared/device/device-display-name.pipe';
import { UtilityService } from './utility.service';
import { StorageService } from './storage.service';
import { ADPResourceService } from 'digital-core';
import { InventoryWithDevices } from './device-inventory/inventory-with-devices';
import { DeviceInfoFormResult } from 'src/app/modules/device-diagnostics/components/cellular-device-info-form/cellular-device-info-form.component';
import { OktaUserProfile } from 'src/app/model/okta/userClaims';
@Injectable({
  providedIn: 'root'
})
export class DeviceInventoryService {
  private deviceInventoryMiddlewareUrl: string;
  private accessToken: string;
  private deviceInventoryGraphQlUrl: string;
  private useCaching = true;
  private middlewareUrl: string;
  private accountPath: string;
  private isEprismOnly: boolean;

  get GraphQLgetDevices(): string { return 'GraphQLgetDevices' }
  get GraphQLgetInventoryInfo(): string { return 'GraphQLgetInventoryInfo' }
  get GraphQLgetDeviceTypes(): string { return 'GraphQLgetDeviceTypes' }
  get GetEprismDeviceTypesMapping(): any[] { return [
    {code: '', deviceCode: 'SMPH', name: 'Smartphone'},
    {code: 'P', deviceCode: 'SMPH', name: 'Smartphone'},
    {code: 'W', deviceCode: 'SMAR', name: 'Smartwatches'},
    {code: 'T', deviceCode: 'TABL', name: 'Tablets'},
  ]};

  constructor(
    public httpClient: HttpClient,
    public googleAnalyticsService: GoogleAnalyticsService,
    private preInitSettingsService: PreInitSettingsService,
    public oktaAuth: OktaAuthStateService,
    private resourceService: ADPResourceService,
    private pghOktaService: PGHOktaService,
    private utilityService: UtilityService,
    private storageService: StorageService,
  ) {
    this.middlewareUrl = this.preInitSettingsService.getSettings().middleWare.middlewareUrl;
    this.accountPath = this.preInitSettingsService.getSettings().middleWare.accountPath;
    this.deviceInventoryMiddlewareUrl = this.preInitSettingsService.getSettings().middleWare.deviceInventoryMiddlewareUrl;
    this.deviceInventoryGraphQlUrl = `${this.deviceInventoryMiddlewareUrl}/graphql`;
    this.accessToken = this.pghOktaService.getAccessToken();
    const hasP360MobilePolicy = this.storageService.getLocal<string>(`EprismContractsCount`) > '0';
    const hasP360HomeTechPolicy = this.storageService.getLocal<string>(`ElitaContractsCount`) > '0';
    this.isEprismOnly = hasP360MobilePolicy && !hasP360HomeTechPolicy;
  }

  /**
   *
   * @deprecated Do not use this function for new development. This uses the
   * old API from the mobile app team. The new GraphQL APIs should be used instead.
   *
   * @returns
   */
  getDeviceInventoryCategories(useCache?: boolean): Observable<ICategory[]> {
    useCache = useCache ?? this.useCaching;

    const cacheKey = 'getDeviceInventoryCategories';
    if (useCache && sessionStorage.getItem(cacheKey)) {
      return of(JSON.parse(sessionStorage.getItem(cacheKey)));
    }

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.accessToken}`
      }),

    };

    return this.httpClient.get<ICategory[]>(`${this.deviceInventoryMiddlewareUrl}/categories`, httpOptions)
      .pipe(
        map(res => {
          if (useCache) {
            sessionStorage.setItem(cacheKey, JSON.stringify(res));
          }
          return res;
        }),
      );;
  }

  /**
   *
   * @param deviceData
   *
   * @param inventoryId The id of the device inventory to add a device to
   *
   * @param catalogSource This is the source of the device information; like where it is coming from.
   * We use "USER" when the user is adding a device manually
   * We use "PTP" when the device comes from the ALDCS (Assurant Labs Device Content System)
   * We use "FING" when the device comes from this other catalog.
   * We use "ESS" when the device comes from the ESS (Equipment Search Service)
   *
   * @returns
   */
  public buildCreateDeviceBodyDtoFromPghUserDeviceBase(deviceData: PghUserDeviceBase, inventoryId: number, catalogSource: 'USER' | 'PTP' | 'FING' | 'ESS'): CreateDeviceBodyDto {
    return {
      catalogSource: catalogSource,
      inventoryId: inventoryId,
      deviceTypeCode: deviceData.deviceTypeCode,
      make: deviceData.make,
      model: deviceData.model,
      modelIdentifier: deviceData.modelIdentifier,
      description: buildDeviceDisplayName(deviceData),
      nickName: deviceData.nickName,
      serialNumber: deviceData.serialNumber,
      purchasePrice: deviceData.purchasePrice,
      purchaseDate: deviceData.purchaseDate?.toString()
    };
  }

  public buildCreateDeviceBodyDtoFromSerialSearchEquipmentInfo(
    inventoryId: number,
    deviceFormResult: DeviceInfoFormResult,
    imei: string,
    serialNumber: string,
    deviceTypeCode: string
  ): CreateDeviceBodyDto {
    return {
      catalogSource: 'ESS',
      inventoryId: inventoryId,
      deviceTypeCode: deviceTypeCode,
      diagnosticStatus: "PENDING",
      modelIdentifier: deviceFormResult.equipmentInfo?.ModelIdentifier,
      make: deviceFormResult.equipmentInfo?.Manufacturer,
      model: deviceFormResult.equipmentInfo?.MarketingName,
      description: deviceFormResult.equipmentInfo?.Description,
      imei: imei,
      serialNumber: serialNumber,
      carrier: deviceFormResult.equipmentInfo?.Attributes?.Carrier,
      color: deviceFormResult.equipmentInfo?.Attributes?.Color ?? deviceFormResult.equipmentInfo?.Attributes?.Material,
      storageSize: deviceFormResult.equipmentInfo?.Attributes?.StorageSize,
      nickName: deviceFormResult.deviceNickname
    };
  }

  public buildPghUserDeviceBaseFromDeviceDto(deviceData: DeviceDto): PghUserDeviceBase {
    if (!deviceData) {
      return null;
    }
    const retVal: PghUserDeviceBase = {
      deviceId: deviceData.id,
      deviceTypeCode: deviceData.deviceType?.code,
      deviceTypeName: deviceData.deviceType?.name,
      hasReceipt: deviceData.hasReceipt,
      inventoryId: deviceData.inventoryId,
      make: deviceData.make,
      model: deviceData.model,
      modelIdentifier: deviceData.modelIdentifier,
      carrier: deviceData.carrier,
      imei: deviceData.imei,
      serialNumber: deviceData.serialNumber,
      lineNumber: deviceData.lineNumber,
      displayName: deviceData.description,
      nickName: deviceData.nickName,
      purchaseDate: deviceData.purchaseDate,
      purchasePrice: deviceData.purchasePrice,
      deviceSource: deviceData.deviceSource,
      phoneNumber: deviceData.phoneNumber,
    };

    if (deviceData?.deviceType?.coverages != null) {
      retVal.coverage = {
        hasAccidentalDamage: deviceData?.deviceType?.coverages?.hasAccidentalDamage,
        hasMechanicalDamage: deviceData?.deviceType?.coverages?.hasMechanicalDamage,
        hasLoss: deviceData?.deviceType?.coverages?.hasLoss,
        hasTheft: deviceData?.deviceType?.coverages?.hasTheft,
      };
    }

    return retVal;
  }

  /**
   * Returns the inventory info for the given policy.
   *
   * @param policyCertificateNumber
   * @param programCode Example: organic_pgh
   * @param dealerCode Example: PGHO
   */
  public getInventoryInfo(policyCertificateNumber: string, programCode: string, dealerCode: string, useCache?: boolean): Observable<InventoryInfoDto> {
    if (this.isEprismOnly) {
      return of(undefined);
    }

    useCache = useCache ?? this.useCaching;

    const cacheKey = `${this.GraphQLgetInventoryInfo}${policyCertificateNumber}${programCode}${dealerCode}`;
    if (useCache && sessionStorage.getItem(cacheKey)) {
      return of(JSON.parse(sessionStorage.getItem(cacheKey)));
    }

    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': `Bearer ${this.accessToken}`,
      'Accept': 'application/json'
    });

    const graphql = JSON.stringify({
      query: `
        mutation createOrGetInventory ($input: CreateInventoryInput!) {
          createOrGetInventory (input: $input) {
            id
            dealerCode,
            policyCertificateNumber,
            program {
              code,
            },
            createdAt,
            updatedAt
          }
      }`,
      variables: {
        input: {
          program: {
            code: programCode
          },
          policyCertificateNumber: policyCertificateNumber,
          dealerCode: dealerCode,
        },
      },
    });

    return this.httpClient.post<any>(this.deviceInventoryGraphQlUrl + "?inventory", graphql, { headers: headers })
      .pipe(
        map(res => {
          this.assertGraphQlResponseIsErrorFree(res, this.deviceInventoryGraphQlUrl);
          if (useCache) {
            sessionStorage.setItem(cacheKey, JSON.stringify(res?.data?.createOrGetInventory));
          }
          return res?.data?.createOrGetInventory;
        }),
      );
  }

  /**
   * This returns device types that work with the Device Inventory.
   *
   * For each device type that is returned, it will include a coverages object
   * that indicate which, if any, coverages the devices of this type have. And
   * the coverages are driven by the inventoryId. So it takes into account that
   * one inventory id might have different coverages than another inventory id.
   *
   * @param inventoryId The id of the inventory. See getInventoryInfo(...)
   *
   * @param typesWithCoverageOnly When this is set to true, then only the
   * device types that have at least one type of coverage will be returned;
   * when this is set to false, then it will not restrict the device types
   * that are returned. When this is not specified at all, then the default
   * value of false will be used.
   *
   * NOTE: When this is set to true, and the inventoryId is an inventoryId
   * of an Elita policy, then only the device types that are covered by that
   * Elita policy will be returned. So if the Elita policy only covers
   * smartwatches and tablets, then only the two device types for
   * smartwatches and tablets will be returned.
   *
   * @param useCache When this is true, then it will retrieve the list of
   * device types from the sessionStorage cache. When this is false, then
   * a call to the Device Inventory API will be made instead of pulling
   * from cache. When this is not specified then it will default to the
   * value of this.useCaching.
   *
   * @returns
   */
  public getDeviceTypes(inventoryId: number, typesWithCoverageOnly: boolean = false, useCache?: boolean): Observable<DeviceTypeDto[]> {
    if (this.isEprismOnly) {
      return of([]);
    }
    useCache = useCache ?? this.useCaching;
    typesWithCoverageOnly = typesWithCoverageOnly ?? false;

    const cacheKey = `${this.GraphQLgetDeviceTypes}-${inventoryId}${typesWithCoverageOnly ? '-coveredOnly' : ''}`;
    if (useCache && sessionStorage.getItem(cacheKey)) {
      return of(JSON.parse(sessionStorage.getItem(cacheKey)));
    }

    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${this.accessToken}`,
      Accept: 'application/json'
    });

    const data = JSON.stringify({
      query: `
        query deviceTypesCovered ($input: DeviceTypeInput!){
          deviceTypes(input: $input) {
            id
            code
            name
            coverages {
              hasAccidentalDamage
              hasMechanicalDamage
              hasLoss
              hasTheft
            }
            createdAt
            updatedAt
          }
      }`,
      variables: {
        input: {
          inventoryId: inventoryId,
          coveredOnly: typesWithCoverageOnly
        }
      }
    });

    return this.httpClient.request<any>('post', this.deviceInventoryGraphQlUrl,
      {
        body: data,
        context: new HttpContext(),
        responseType: <any>'json',
        withCredentials: false,
        headers,
        observe: 'response',
        reportProgress: false
      }
    ).pipe(
      map(res => {
        this.assertGraphQlResponseIsErrorFree(res?.body, this.deviceInventoryGraphQlUrl);
        if (useCache) {
          sessionStorage.setItem(cacheKey, JSON.stringify(res?.body?.data?.deviceTypes));
        }
        return res?.body?.data?.deviceTypes;
      }),
    );
  }

  public getEprismDeviceTypes(phoneNumber: string): Observable<DeviceTypeDto> {
    return this.httpClient.get<Deductible[]>(`${this.middlewareUrl}${this.accountPath}deductibles/${phoneNumber}`)
      .pipe(map(deductibles => {
        let deviceType: DeviceTypeDto = {
          coverages: {
            hasAccidentalDamage: !!deductibles.find(d => d.lossType == 'Physical Damage'),
            hasMechanicalDamage: !!deductibles.find(d => d.lossType == 'Mechanical Breakdown'),
            hasLoss: !!deductibles.find(d => d.lossType == 'Missing or Lost'),
            hasTheft: !!deductibles.find(d => d.lossType == 'Missing or Lost'),
            hasScreenDamage: !!deductibles.find(d => d.lossType == 'Physical Damage-Cracked Screen/Display'),
          },
          deductibleAmounts: {
            accidentalDamageAmount: deductibles.find(d => d.lossType == 'Physical Damage')?.deductibleAmount,
            mechanicalDamageAmount: deductibles.find(d => d.lossType == 'Mechanical Breakdown')?.deductibleAmount,
            lossTheftAmount: deductibles.find(d => d.lossType == 'Missing or Lost')?.deductibleAmount,
            screenDamageAmount: deductibles.find(d => d.lossType == 'Physical Damage-Cracked Screen/Display')?.deductibleAmount,
          }
        };
        return deviceType;
      }));
  }

  public getEprismDevices(): DeviceDto[] {
    let eprismContracts = this.storageService.getLocal<any[]>('EprismContracts');
    let oktaProfile = this.storageService.getLocal<OktaUserProfile>('okta-user-profile');
    let eprismDevices: DeviceDto[] = [];
    if (eprismContracts?.length) {
      let contract = eprismContracts.find(x => x.policyIdentifier.mobileNumber == oktaProfile.primaryPhone);
      let isPrimary = contract.policyHolderInfo.pahPhone == contract.policyIdentifier.mobileNumber;
      if (!isPrimary) {
        eprismContracts = eprismContracts.filter(x => x.policyIdentifier.mobileNumber == contract.policyIdentifier.mobileNumber);
      }
      eprismContracts.forEach(e => {
        let index = 0;
        let modelDescSplit = e.deviceInfo.description?.split(' ');
        eprismDevices.push({
          imei: e.deviceInfo.serialType?.toLowerCase() == 'imei' ? e.deviceInfo.serialNumber : '',
          id: e.policyIdentifier.policyNumber,
          inventoryId: e.policyIdentifier.accountNumber, //BAN Number
          catalogSource: 'EPRISM',
          make: e.deviceInfo.make,
          model: this.extractModelName(e.deviceInfo.modelDescription),
          description: e.deviceInfo.modelDescription,
          serialNumber: e.deviceInfo.serialNumber,
          deviceType: {
            code: this.GetEprismDeviceTypesMapping.find(x => x.code == e.deviceInfo.deviceType).deviceCode,
            name: this.GetEprismDeviceTypesMapping.find(x => x.code == e.deviceInfo.deviceType).name,
            id: index,
            coverages: null,
          },
          purchaseDate: e.deviceInfo.devicePurchaseDate,
          purchasePrice: null, // Is this the one: retailPricePaid ?
          color: modelDescSplit?.length > 2 ? modelDescSplit[modelDescSplit.length-1] : null,
          storageSize: modelDescSplit?.length > 2 ? modelDescSplit[modelDescSplit.length-2] : null,
          createdAt: null,
          updatedAt: null,
          deviceSource: 'EPRISM',
          nickName: e.deviceInfo.make + ' ' + this.extractModelName(e.deviceInfo.modelDescription),
          phoneNumber: e.policyIdentifier.mobileNumber,
        });
        index++;
      });
    }
    return eprismDevices;
  }

  public getDevices(inventoryId: number, useCache?: boolean): Observable<DeviceDto[]> {
    if (this.isEprismOnly) {
      return of([]);
    }
    useCache = useCache ?? this.useCaching;

    const cacheKey = `${this.GraphQLgetDevices}${inventoryId}`;
    if (useCache && sessionStorage.getItem(cacheKey)) {
      return of(JSON.parse(sessionStorage.getItem(cacheKey)));
    }

    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': `Bearer ${this.accessToken}`,
      'Accept': 'application/json'
    });

    const graphql = JSON.stringify({
      query: `
        query devices ($input: DeviceInput!){
          devices(input: $input) {
            id
            inventoryId,
            modelIdentifier,
            make,
            model,
            description,
            nickName,
            deviceTypeId,
            carrier,
            diagnosticStatus,
            imei,
            serialNumber,
            lineNumber,
            color,
            storageSize,
            isActive,
            elitaDeviceId
            deviceType {
              id,
              code,
              name,
              coverages {
                hasAccidentalDamage,
                hasMechanicalDamage,
                hasLoss,
                hasTheft,
              },
            },
            catalogSource,
            hasReceipt,
            createdAt,
            updatedAt
          }
      }`,
      variables: {
        input: {
          inventoryId: inventoryId
        },
      },
    });

    return this.httpClient.post<any>(this.deviceInventoryGraphQlUrl + "?devices", graphql, { headers: headers })
      .pipe(
        map(res => {
          this.assertGraphQlResponseIsErrorFree(res, this.deviceInventoryGraphQlUrl);
          if (useCache) {
            if (sessionStorage.getItem('Disable_Device_Cache') != 'true') {
              sessionStorage.setItem(cacheKey, JSON.stringify(res?.data?.devices));
            }
          }
          return res?.data?.devices;
        })
      );
  }

  public createDevice(device: CreateDeviceBodyDto): Observable<DeviceDto> {
    if (this.isEprismOnly) {
      return of(undefined);
    }

    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': `Bearer ${this.accessToken}`,
      'Accept': 'application/json'
    });
    // When you create or Update a device the device is returned from the graphQL service.
    // The device coming back from the service will not include the encoded chars.
    // So if you update with AT&amp;T and then get the device back from the graphQL it will be AT&T.
    this.replaceAmpersands(device);
    const graphql = JSON.stringify({
      query: `
        mutation createDevice ($input: CreateDeviceInput!) {
          createDevice (input: $input) {
            id
            inventoryId
            make
            model
            modelIdentifier
            description
            nickName
            deviceTypeId
            carrier
            diagnosticStatus
            imei
            serialNumber
            lineNumber
            color
            storageSize
            isActive
            elitaDeviceId
            deviceType {
              id,
              name,
              code,
              coverages {
                hasAccidentalDamage,
                hasMechanicalDamage,
                hasLoss,
                hasTheft,
              },
            },
            catalogSource
            hasReceipt
            createdAt
            updatedAt
          }
      }`,
      variables: {
        input: device,
      },
    });

    return this.httpClient.post<any>(this.deviceInventoryGraphQlUrl, graphql, { headers: headers })
      .pipe(
        map(res => {
          this.assertGraphQlResponseIsErrorFree(res, this.deviceInventoryGraphQlUrl);
          this.clearCache();
          return res?.data?.createDevice;
        }),
      );
  }

  public updateDevice(device: UpdateDeviceBodyDto): Observable<DeviceDto> {
    if (this.isEprismOnly) {
      return of(undefined);
    }
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': `Bearer ${this.accessToken}`,
      'Accept': 'application/json'
    });
    this.replaceAmpersands(device);

    const graphql = JSON.stringify({
      query: `
        mutation updateDevice ($input: UpdateDeviceInput!) {
          updateDevice (input: $input) {
            id
            inventoryId
            make
            model
            modelIdentifier
            description
            nickName
            deviceTypeId
            carrier
            diagnosticStatus
            imei
            serialNumber
            lineNumber
            color
            storageSize
            isActive
            elitaDeviceId
            deviceType {
              id,
              name,
              code,
              coverages {
                hasAccidentalDamage,
                hasMechanicalDamage,
                hasLoss,
                hasTheft,
              },
            },
            catalogSource
            hasReceipt
            createdAt
            updatedAt
          }
      }`,
      variables: {
        input: device,
      },
    });

    return this.httpClient.post<any>(this.deviceInventoryGraphQlUrl, graphql, { headers: headers })
      .pipe(
        map(res => {
          this.assertGraphQlResponseIsErrorFree(res, this.deviceInventoryGraphQlUrl);
          this.clearCache();
          return res?.data?.updateDevice;
        }),
      );
  }

  public deleteDevice(deviceId: number, inventoryId: number): Observable<DeviceDto> {
    if (this.isEprismOnly) {
      return of(undefined);
    }
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': `Bearer ${this.accessToken}`,
      'Accept': 'application/json'
    });

    const graphql = JSON.stringify({
      query: `
        mutation deleteDevice ($input: DeviceInput!) {
          deleteDevice (input: $input) {
            id
            inventoryId
            modelIdentifier
            make
            model
            description
            nickName
            deviceTypeId
            carrier
            diagnosticStatus
            imei
            serialNumber
            lineNumber
            color
            storageSize
            isActive
            elitaDeviceId
            deviceType {
              id,
              name,
              code,
              coverages {
                hasAccidentalDamage,
                hasMechanicalDamage,
                hasLoss,
                hasTheft,
              },
            },
            catalogSource
            hasReceipt
            createdAt
            updatedAt
          }
      }`,
      variables: {
        input: {
          inventoryId: inventoryId,
          id: deviceId,
        },
      },
    });

    return this.httpClient.post<any>(this.deviceInventoryGraphQlUrl, graphql, { headers: headers })
      .pipe(
        map(res => {
          this.assertGraphQlResponseIsErrorFree(res, this.deviceInventoryGraphQlUrl);
          this.clearCache(`${this.GraphQLgetDevices}${inventoryId}`);
          return res?.data?.deleteDevice;
        }),
      );
  }

  public clearCache(cacheKey: string = undefined): void {
    if (cacheKey) {
      sessionStorage.removeItem(cacheKey);
      return;
    }
    // Get all keys in sessionStorage
    const keysToDelete = Object.keys(sessionStorage);

    // Filter and keep only keys that start with 'GraphQL'
    const keysStartingWithGraphQL = keysToDelete.filter(key => key.startsWith('GraphQL'));

    // Delete the selected keys
    for (const key of keysStartingWithGraphQL) {
      sessionStorage.removeItem(key);
    }
  }

  private assertGraphQlResponseIsErrorFree(response: any, url: string) {
    // If the devices are returned then allow the user to proceed.
    if ((response?.data?.devices?.length ?? 0) > 0) {
      return;
    }
    // If there are no devices and an error exists, show the error message.
    if ((response?.errors?.length ?? 0) > 0) {
      throw new HttpErrorResponse({
        error: response,
        status: 400,
        url: url,
      });
    }
  }

  public addPhoneNumberToInventory(lineNumber: string, inventoryId: number, newUser: boolean, nickname?: string, make?: string, model?: string) {
    if (this.isEprismOnly) {
      return of(undefined);
    }

    lineNumber = this.utilityService.getPhoneNumberInE164(lineNumber);
    var device: CreateDeviceBodyDto = {
      inventoryId: inventoryId,
      lineNumber: lineNumber,
      description: "Invited through MyAccount",
      nickName: nickname ? nickname : "My Phone - " + Math.floor(Math.random() * 1000),
      catalogSource: "USER",
      diagnosticStatus: "PENDING",
      deviceTypeCode: "SMPH",
      make: make,
      model: model
    };
    return this.createDevice(device).pipe(
      map(res => {
        if (newUser) {
          this.storageService.setSession<DeviceDto>('NewUserDevice', res);
        }
        this.clearCache();
        return res;
      })
    );
  }

  // Only Call for New Phone Device Flow
  public updateNewPhoneNumberDetails(lineNumber: string, imei: string, carrier: string, nickName: string) {
    if (this.isEprismOnly) {
      return of(undefined);
    }

    let newUserPhoneDevice = this.storageService.getSession<DeviceDto>('NewUserDevice');
    if (!newUserPhoneDevice) {
      return null;
    }
    lineNumber = this.utilityService.getPhoneNumberInE164(lineNumber);
    const deviceToUpdate: UpdateDeviceBodyDto = {
      id: newUserPhoneDevice.id,
      lineNumber: lineNumber,
      inventoryId: newUserPhoneDevice.inventoryId,
      carrier: carrier,
      imei: imei,
      nickName: nickName,
      deviceTypeCode: "SMPH" // PBI added to backlog to capture Device Type Code
    };
    return this.updateDevice(deviceToUpdate).pipe(
      map(res => {
        this.clearCache();
        return res;
      })
    );
  }

  public getCarrierList(countryIso: string): Observable<any> {
    return this.httpClient.get<boolean>(`${this.middlewareUrl}${this.accountPath}carriers/${countryIso}`);
  }

  public getInventoryWithDevices(policyCertificateNumber: string, programCode: string, dealerCode: string, useCache?: boolean) {
    if (this.isEprismOnly || !policyCertificateNumber) {
      return of(undefined);
    }
    return this.getInventoryInfo(policyCertificateNumber, programCode, dealerCode, useCache)
      .pipe(
        concatMap(inventory => forkJoin([of(inventory), this.getDevices(inventory.id, useCache)])),
        map(([inventory, devices]): InventoryWithDevices => ({ devices: devices, info: inventory })),
      );
  }

  private replaceAmpersands(device: UpdateDeviceBodyDto | CreateDeviceBodyDto) {
    const isElita = this.resourceService.getPortalSetting('Global_CTC_IsElita').Value;
    if (!isElita || isElita === 'false') {
      return;
    }
    // If we are using Elita, we need to make sure that we do not send any &'s in a text field.
    // The SOAP endpoint will throw an error.
    if (device?.carrier?.indexOf('&') > -1) {
      device.carrier = device.carrier.replace('&', '&amp;');
    }
    if (device?.make?.indexOf('&') > -1) {
      device.make = device.make.replace('&', '&amp;');
    }
    if (device?.model?.indexOf('&') > -1) {
      device.model = device.model.replace('&', '&amp;');
    }
    if (device?.description?.indexOf('&') > -1) {
      device.description = device.description.replace('&', '&amp;');
    }
    if (device?.color?.indexOf('&') > -1) {
      device.color = device.color.replace('&', '&amp;');
    }
    if (device?.storageSize?.indexOf('&') > -1) {
      device.storageSize = device.storageSize.replace('&', '&amp;');
    }
    if (device?.nickName?.indexOf('&') > -1) {
      device.nickName = device.nickName.replace('&', '&amp;');
    }
    if (device?.serialNumber?.indexOf('&') > -1) {
      device.serialNumber = device.serialNumber.replace('&', '&amp;');
    }
  }

  // TODO:Eprism - this is temprory and this is going to be removed once the new model-name field is added by eprism team
  private extractModelName(description: string): string {
    //description = 'IPhone 15 Pro 512GB Black';
    // Find the position of "GB" in the description
    const gbIndex = description.toUpperCase().indexOf('GB');
    // If "GB" is not found, return the original description
    if (gbIndex === -1) {
      return description.trim();
    }
    // Extract everything before "GB" and the storage number
    // Look backwards from GB to find where the storage size starts
    let storageStartIndex = gbIndex;
    // Move backwards to find the start of the storage number
    while (storageStartIndex > 0 && /[\d\s]/.test(description[storageStartIndex - 1])) {
      storageStartIndex--;
    }
    // Extract the model name (everything before the storage size)
    const modelName = description.substring(0, storageStartIndex).trim();
    return modelName;
  }
}
